import defaults from "./defaults.js";
import { Globals } from "./globals.js";
import { WordList } from "./wordlist.js";
import { TagList } from "./vars.js";

/**************************************************************************************************

   ########  ########  ######   #######  ##     ## ########   ######  ######## 
   ##     ## ##       ##    ## ##     ## ##     ## ##     ## ##    ## ##       
   ##     ## ##       ##       ##     ## ##     ## ##     ## ##       ##       
   ########  ######    ######  ##     ## ##     ## ########  ##       ######   
   ##   ##   ##             ## ##     ## ##     ## ##   ##   ##       ##       
   ##    ##  ##       ##    ## ##     ## ##     ## ##    ##  ##    ## ##       
   ##     ## ########  ######   #######   #######  ##     ##  ######  ######## 

**************************************************************************************************/

export class Resource {
    constructor(name, type, url, scene, callback) {
        this.name = name;
        this.type = type;
        this.url = url;
        this.scene = scene;
        this.rawContent = false;
        this.formattedContent = false;
        this.loading = true;
        this.callback = callback;
    }

    async load() {
        if (this.callback) {
            this.callback(1);
        }
        try {
            const response = await fetch(`${defaults.CORS_PROXY}${this.url}`);
            if (!response.ok) {
                Globals.log.error(`Failed to fetch URL: ${response.status} ${response.statusText}`);
            } else {
                this.rawContent = await response.text();
            }
        } catch (error) {
            Globals.log.error("Failed to fetch URL: " + error.message);
            return;
        }
        switch( this.type ) {
            case "text":
            case "file":
            case "data":
            case "array":
                this.formattedContent = this.rawContent.split(/\r?\n/);
                break;
            case "html":
                {
                    const parser = new DOMParser();
                    this.formattedContent = parser.parseFromString(this.rawContent, 'text/html');
                }
                break;
            case "rss":
            case "xml":
                {
                    const parser = new DOMParser();
                    this.formattedContent = parser.parseFromString(this.rawContent, 'text/xml');
                }
                break;
            default:
                break;
        }
        if (this.callback) {
            this.callback();
        }
        this.loading = false;
    }

    setVar(varName, pattern, item) {
        const itemNo = parseInt(item) - 1;
        switch(this.type) {
            case "text":
            case "file":
                if (itemNo < 0 || itemNo >= this.rawContent.length) {
                    this.scene.varList.setValue(varName, constants.NOTFOUND);
                    break;
                }
                this.scene.varList.setValue(varName, this.formattedContent[itemNo]);
                break;
            case "html":
            case "rss":
            case "xml":
                if (pattern == "") {
                    pattern = "*";
                }
                const items = this.formattedContent.querySelectorAll(pattern);
                if (itemNo < 0 || itemNo >= items.length) {
                    this.scene.varList.setValue(varName, constants.NOTFOUND);
                } else {
                    this.scene.varList.setValue(varName, items[itemNo].innerHTML);
                }
                break;
            case "data":
            case "array":
                for (let i = 0; i < this.formattedContent.length; i++) {
                    const line = this.formattedContent[i];
                    if (line.charAt(0) == "#" || line.length < 3) {
                        continue;
                    }
                    const wordList = new WordList(this.formattedContent[i]);
                    const key = wordList.getWord();
                    const value = wordList.joinWords();
                    if (key && value) {
                        this.varList.setValue(`$varName[$key]`, value);
                    }
                }
                break;
            default:
                break;
        }
    }

    static getResource(sceneName, name) {
        let parts = name.split(":");
        if (parts.length > 1) {
            sceneName = parts[0];
            name = parts[1];
        }
        for ( let i = 0; i < Globals.scenes.length; i++ ) {
            if (Globals.scenes[i].name == sceneName) {
                for ( let j = 0; j < Globals.scenes[i].resources.length; j++ ) {
                    if (Globals.scenes[i].resources[j].name == name) {
                        if (Globals.scenes[i].resources[j].loading) {
                            return("loading");
                        } else {
                            return(Globals.scenes[i].resources[j]);
                        }
                    }
                }
            }
        }
        Globals.log.error("No resource found- " + sceneName + ":" + name);
        return(false);
    }

    static listResources(resources, verbose = true) {
        let text = "";
        for (let i = 0; i < resources.length; i++) {
            const resource = resources[i];
            if (verbose) {
                text += `Name ${resource.name} `;
                text += resource.loading ? "loaded" : "loading";
                text += ` from ${resource.url}\n`;
                text += `Type: ${resource.type} Size: ${resource.rawContent.length}\n`;
            } else {
                text += `${resource.name} `;
            }
        }
        return text;
    }
}

/**************************************************************************************************

    ######   ######   #### ##     ##    ###     ######   ######## 
   ##    ## ##    ##   ##  ###   ###   ## ##   ##    ##  ##       
   ##       ##         ##  #### ####  ##   ##  ##        ##       
    ######  ##   ####  ##  ## ### ## ##     ## ##   #### ######   
         ## ##    ##   ##  ##     ## ######### ##    ##  ##       
   ##    ## ##    ##   ##  ##     ## ##     ## ##    ##  ##       
    ######   ######   #### ##     ## ##     ##  ######   ######## 

**************************************************************************************************/

export class SGImage {
    constructor(url, name, callback, cols = 0, rows = 0) {
        this.url = url;
        this.name = name;
        this.width = 0;
        this.height = 0;
        this.origWidth = 0;
        this.origHeight = 0;
        this.callback = callback;
        // check for silly cell sizes
        if (cols < 0) {
            cols = 0;
        } else if (rows < 1) {
            rows = 1;
        }
        this.cols = cols; // animation frames
        this.rows = rows;
        this.tags = new TagList();
        this.piImage = null;
        if (callback) {
            callback(1);
        }
        this.loading = true;
    }

    async loadImage() {
        this.piImage = await PIXI.Assets.load(this.url);
        this.loading = false;
        if (this.callback) {
            // Globals.log.report(`Image ${this.name} loaded`);
            this.callback(-1);
        }
        this.width = this.piImage.width;
        this.height = this.piImage.height;
        this.origWidth = this.width;
        this.origHeight = this.height;
    }

    static getImage(sceneName, name) {
        let parts = name.split(":");
        if (parts.length > 1) {
            sceneName = parts[0];
            name = parts[1];
        }
        for ( let i = 0; i < Globals.scenes.length; i++ ) {
            if (Globals.scenes[i].name == sceneName) {
                for ( let j = 0; j < Globals.scenes[i].images.length; j++ ) {
                    if (Globals.scenes[i].images[j].name == name) {
                        if (Globals.scenes[i].images[j].loading) {
                            return("loading");
                        } else {
                            return(Globals.scenes[i].images[j]);
                        }
                    }
                }
            }
        }
        Globals.log.error("No image found- " + sceneName + ":" + name);
        return(null);
    }

    constrainFrame(frameNo) {
        const numFrames = this.cols * this.rows;
        if (frameNo > numFrames) {
            frameNo = 1;
        } else if (frameNo < 1) {
            frameNo = numFrames;
        }
        return frameNo;
    }

    makeCellRect(frameNo) {
        const numFrames = this.cols * this.rows;
        // const column = Math.floor(number / (this.columns + 1));
        const column = ((frameNo - 1) % this.cols);
        const row = Math.floor((frameNo -1) / this.cols)
        const frameWidth = this.width / this.cols;
        const frameHeight = this.height / this.rows;
        const frameX = column * frameWidth;
        const frameY = row * frameHeight;
        return new PIXI.Rectangle(frameX, frameY, frameWidth, frameHeight);
    }

    static listImages(images, verbose = true) {
        let text = "";
        for (let i = 0; i < images.length; i++ ) {
            const image = images[i];
            if (verbose) {
                const width = images[i].piImage.width;
                const height = images[i].piImage.height;
                text += `Name ${image.name} `;
                text += image.loading ? "loaded" : "loading";
                text += ` from ${image.url}\n`;
                text += `Width: ${width} Height: ${height}\n`;
            } else {
                text += `${image.name} `;
            }
        }
        return text;
    }

    static listImageTags(images, tag) {
        if (!Array.isArray(tag)) {
            tag = [tag];
        }
        let result = "";
        let first = true;
        for (let i = 0; i < images.length; i++ ) {
            for (let j = 0; j < tag.length; j++) {
                if (images[i].tags.hasTag(tag[j])) {
                    if (first) {
                        first = false;
                    } else {
                        result += " ";
                    }
                    result += images[i].name;
                    break; // don't need to check the rest of tags
                }
            }
        }
        return result;
    }

}
